import { useCallback, useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FilterMatchMode } from 'primereact/api';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { notaCreditoSchema, businessSchema } from '../../validation/addNotaCreditoValidations';
import { buscarComprobante } from '../../services/inventarioService';
import { dateISOString, formatDateTime, formatFecha } from '../../utils/dateFormatter';
import { formatearMoneda } from '../../utils/numberFormatter';
import './agregar-nota-credito.css';
import useMedia from '../../hooks/media-querys'

export default function AgregarNotaCreditoModal({ visible, onHide, onSubmit }) {
  const isPhone = useMedia('(max-width: 640px)');
  const isTablet = useMedia('(max-width: 1024px)');
  const dtResponsive = (isTablet || isPhone) ? 'stack' : 'scroll'; // DataTable

  const toast = useRef(null);
  const [comprobanteCod, setComprobanteCod] = useState(''); // Estado para almacenar el código del comprobante buscado
  const [comprobantes, setComprobantes] = useState([]); // Estado para almacenar la respuesta de comprobantes
  const [selectedComprobante, setSelectedComprobante] = useState(null); // Estado para almacenar el comprobante seleccionado
  const [productos, setProductos] = useState([]); // Estado para almacenar los productos asociados al comprobante
  const [selectedProductos, setSelectedProductos] = useState([]); // Estado para almacenar los productos seleccionados para la nota de crédito
  const [cantidades, setCantidades] = useState({}); // Estado para almacenar las cantidades a descontar de productos seleccionados
  const [fechaMin, setFechaMin] = useState(null);
  const fechaMax = new Date().toISOString().split('T')[0];
  
  const [filters, setFilters] = useState({
    global: { 
      value: null, 
      matchMode: FilterMatchMode.CONTAINS 
    }
  });
  const [filteredvalue, setFilteredvalue] = useState([]);
  const [globalFilterValue, setGlobalFilterValue] = useState('');

  // Inicialización formulario nota de credito
  const { register, setValue, getValues, handleSubmit, reset, watch, 
    formState: { errors, isValid, isSubmitting }
  } = useForm({ 
    resolver: yupResolver(notaCreditoSchema), 
    defaultValues: { tipo: 'Nota Crédito', precioNeto: 0 },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;

    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);

    // Registros filtrados
    const filtrado = productos.filter((item) => {
      return Object.values(item).some((val) =>
        String(val).toLowerCase().includes(value.toLowerCase())
      );
    });
    setFilteredvalue(filtrado);
  };

  // Renderizar encabezado tabla de comprobantes
  const renderHeaderComprobantes = () => (
    <div className="flex flex-wrap gap-2 align-items-center justify-content-start">
      <h6 className="m-0">Ingrese N° Factura</h6>
      <div className='flex gap-2 align-items-center justify-content-end'>
        <InputText
          className='input-text-s'
          style={{ color:'#1d4ed8', fontSize: 16, fontWeight: 'bold'}} 
          placeholder='Ej. 1012340'
          type='text' keyfilter="int"
          value={comprobanteCod} 
          onChange={(e) => setComprobanteCod(e.target.value)}
        />
        <Button 
          icon="pi pi-search"
          label={isPhone ? null : "Buscar"} 
          className="inv-btn-buscar"
          onClick={handleBuscar}
        />
      </div>
    </div>
  );

  // Buscar comprobante por código
  const handleBuscar = async () => {
    if (!comprobanteCod) {
      toast.current.show({ severity: 'warn', summary: 'Advertencia', life: 4000, detail: 'Ingrese un código de comprobante para realizar la busqueda.' });
      return;
    }

    try {
      const data = await buscarComprobante(comprobanteCod);

      const comprobante = data.comprobante;
      
      if (comprobante) {
        const formatData = {...comprobante, fechaEmision: formatFecha(new Date())};
        setComprobantes([formatData]);
      }
    } catch (error) {
      setComprobantes(null);
      toast.current.show({ severity: 'error', summary: 'Error', life: 4000, detail: error || 'Error al buscar el comprobante' });
    }
  };

  // Función para manejar la selección de un comprobante
  const handleSeleccionComprobante = useCallback((comprobante) => {
    setSelectedComprobante(comprobante);
    if (comprobante) {
      setProductos(comprobante.productos || []);
      setValue('nombreEmpresa', comprobante.nombreEmpresa || '');
      setValue('rutEmpresa', comprobante.rutEmpresa || '');
      
      const fechaMin = dateISOString(comprobante.fechaEmision)
      setFechaMin(fechaMin);

      // Valida y ajusta la fecha del input si está fuera del rango
      const fechaActual = getValues('fecha');
      if (!fechaActual || (fechaMin && fechaActual < fechaMin)) {
        setValue('fecha', fechaMin);
      }

    } else {
      setProductos([]);
      setValue('nombreEmpresa', '');
      setValue('rutEmpresa', '');
      setValue('precioNeto', 0);
      setValue('fecha', null)
      setFechaMin(null);
    }
  }, [setValue, getValues]);

  // Renderizar encabezado tabla de productos
  const renderHeaderProductos = () => (
    <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
      <h6 className="m-0">Seleccione productos a descontar</h6>
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search pr-2 pt-1" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          style={{ height: '2rem', paddingLeft: '2rem' }}
          placeholder="Buscar en la tabla" />
      </IconField>
    </div>
  );

  const onDescontarChange = (productoId, rawValue, min = 1, max) => {
    // Normaliza: InputNumber puede entregar null, undefined o NaN
    let v = Number(rawValue ?? 1);
    if (Number.isNaN(v)) v = 1;
    // Aplica límites
    if (min != null) v = Math.max(v, min);
    if (max != null) v = Math.min(v, max);

    setCantidades(prev => ({ ...prev, [productoId]: v }));
  };

  // Renderiza el campo de descuento para cada fila (producto)
  const descontarTemplate = (rowData) => {
    const id = rowData.productoId;
    const min = 1;
    const max = maxDescontar(rowData);
    const permitido = canDescontar(rowData);
    
    const value = cantidades[id] ?? 1;
    const estaSeleccionado = selectedProductos.some(p => p.productoId === id);

    if (!permitido) {
      return <small><span style={{ color: 'red' }}>Stock insuficiente</span></small>;
    }

    return (
      <InputNumber
        value={value}
        disabled={!estaSeleccionado || !permitido}
        inputMode="numeric"
        onValueChange={(e) => onDescontarChange(id, e.value, min, max)}
        onBlur={(e) => onDescontarChange(id, e.target.value, min, max)}
        onKeyUp={(e) => onDescontarChange(id, e.target.value, min, max)}
        mode="decimal"
        minFractionDigits={0}
        maxFractionDigits={0}
        min={min}
        max={max}
        inputStyle={{ width: isPhone ? '4rem' : '4.5rem', height: '2rem', textAlign: 'center' }}
      /> 
    )
  };

  // Al cambiar de comprobante
  useEffect(() => {
    if (selectedComprobante) {
      setProductos(selectedComprobante.productos || []);
      setValue('nombreEmpresa', selectedComprobante.nombreEmpresa || '');
      setValue('rutEmpresa', selectedComprobante.rutEmpresa || '');
    }
  }, [selectedComprobante, setValue]);

  // Limpiar cantidades de los no seleccionados
  useEffect(() => {
    setCantidades(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => {
        if (!selectedProductos.find(p => p.productoId === Number(id))) {
          delete next[id];
        }
      });
      return next;
    });
  }, [selectedProductos]);

  // Recalcular total y volcar en el form (precioNeto)
  useEffect(() => {
    const total = selectedProductos.reduce((sum, p) => {
      const qty = Number(cantidades[p.productoId] ?? 1); 
      const unit = Number(p.precioUnitario) || 0;
      return sum + qty * unit;
    }, 0);

    // Marca el cambio para que el input registrado se refresque
    setValue('precioNeto', total, { shouldDirty: true }); 
  }, [cantidades, selectedProductos, setValue]);

  // Función para manejar la confirmación de la nota de crédito
  const handleConfirmar = async (notaCreditoData) => {
    if (!comprobanteCod) { 
      toast.current.show({ severity: 'info', summary: 'Información', life: 4500, detail: 'Comience realizando una busqueda.' });
      return;
    } else if (!selectedComprobante) {
      toast.current.show({ severity: 'info', summary: 'Información', life: 4000, detail: 'Seleccione un comprobante para continuar.' });
      return;
    }
    const negocio = {
      selectedProductos,
      precioNeto: notaCreditoData.precioNeto
    };

    try {
      await businessSchema.validate(negocio, { abortEarly: false });
    } catch (error) {
      error.inner.forEach(e => {
        toast.current.show({
          severity: 'error',
          summary: 'Error',
          detail: e.message,
          life: 4500
        });
      });
      return;
    }

    const productosNC = selectedProductos.map(p => ({
      movimientoId: p.movimientoId,
      inventarioId: p.inventarioId,
      productoId: p.productoId,
      descontar: cantidades[p.productoId],
      precioUnitario: p.precioUnitario,
      medida: p.medida,
    }));

    const payload = {
      notaCredito: { 
        ...notaCreditoData, 
        fecha: formatDateTime(notaCreditoData.fecha)
      },
      productosNC,
      facturaId: selectedComprobante.comprobanteId,
      // fechaEvento: formatDateTime(new Date()),
    };

    onSubmit(payload);
    limpiarTodo();
    // onHide();
  };

  // Función para limpiar todos los campos y estados
  const limpiarTodo = () => {
    reset();
    setComprobantes([]);
    setComprobanteCod('');
    setProductos([]);
    setSelectedComprobante(null);
    setSelectedProductos([]);
    setCantidades({});
  }

  // Helpers
  const canDescontar = (row) => {
    const stock = Number(row.stockInventario ?? row.stock ?? 0);
    const medida = Number(row.medida ?? 1);
    return stock >= medida; // hay al menos 1 unidad del producto
  };

  const maxDescontar = (row) => {
    const stock = Number(row.stockInventario ?? row.stock ?? 0);
    const medida = Number(row.medida ?? 1);
    if (!medida) return 0;
    return Math.floor(stock / medida); // 0 si no alcanza
  };

  // Productos válidos (con stock suficiente)
  const productosValidosSeleccionados = selectedProductos.filter(canDescontar);
  
  // Cantidades válidas para cada producto válido seleccionado
  const cantidadesValidas = productosValidosSeleccionados.every(p => {
    const qty = Number(cantidades[p.productoId] ?? 0);
    const max = maxDescontar(p);
    return qty >= 1 && qty <= max;
  });
  
  const totalNetoForm = Number(watch('precioNeto') ?? 0);

  const canSubmit =
    isValid &&
    !!selectedComprobante &&
    productosValidosSeleccionados.length > 0 &&
    cantidadesValidas &&
    totalNetoForm > 0;

  return (
    <div>
      <Toast ref={toast} position="center"/>
      <Dialog header="AÑADIR NOTA DE CRÉDITO"
        className="dialog-container"
        visible={visible} blockScroll
        onHide={onHide} position="center"
        style={{ width: isPhone ? '98vw' : isTablet ? '92vw' : '70vw', maxWidth: 1200 }}
        breakpoints={{ '1024px': '95vw', '768px': '98vw' }}
        footer={
          <div className="inv-btns-container gap-2">
            <Button label="Limpiar Todo" icon="pi pi-eraser"  className="inv-btn-cancelar" type="button" onClick={limpiarTodo} />
            <Button 
              type="button"
              label="Confirmar"
              icon="pi pi-check"
              className="inv-btn-confirm"
              disabled={!canSubmit || isSubmitting}
              onClick={handleSubmit(handleConfirmar)}
            />
          </div>
        }
      >
        <div className='form-agregar-nc'> 
          <div className='contenedor-nc'>
            
            <div className='card-nc'>
              <h5>Buscar Factura</h5>
              <DataTable 
                value={comprobantes} 
                header={renderHeaderComprobantes()}
                size='small' stripedRows 
                dataKey="comprobanteId" 
                selectionMode="single"
                selection={selectedComprobante} 
                onSelectionChange={(e) => handleSeleccionComprobante(e.value)}
                breakpoint="960px"
                scrollable={dtResponsive === 'scroll'}
                scrollHeight={isPhone ? '35vh' : isTablet ? '40vh' : '30rem'}
                tableStyle={{ fontSize: isPhone ? '0.8rem' : '0.9rem' }}
                emptyMessage="No hay facturas disponibles."
              >
                <Column field="comprobanteCod" header="Código"></Column>
                <Column field="nombreEmpresa" header="Empresa"></Column>
                <Column field="rutEmpresa" header="Rut Empresa"></Column>
                <Column field="totalNeto" header="Total Neto" body={(rowData) => formatearMoneda(rowData.totalNeto)} ></Column>
                <Column field="fechaEmision" header="Fecha Emisión"></Column>
              </DataTable>
            </div>
            
            <div className='card-nc'>
              <h5>Registrar Nota de crédito</h5>

              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label" style={{ fontSize: 18, fontWeight: 'bold', align: 'center'}} >
                    N° de Comprobante<span className="required">*</span>
                  </label>
                  <input type="text" placeholder='Número del comprobante'
                    style={{ fontSize: 18, background: 'aliceblue'}} 
                    className={`form-control ${errors.codigo ? 'is-invalid' : ''}`}
                    {...register('codigo')}
                  />
                  {errors.codigo && (
                    <div className="invalid-feedback">{errors.codigo.message}</div>
                  )}
                </div>
              </div>

              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label"> Nombre Empresa <span style={{color: '#c5c5c5ff'}}>(automático)</span></label>
                  <input type="text" readOnly 
                    style={{ fontSize: 18}} 
                    placeholder='Nombre Empresa' 
                    className='form-control' 
                    {...register('nombreEmpresa')}
                  />
                </div>
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">RUT Empresa <span style={{color: '#c5c5c5ff'}}>(automático)</span></label>
                  <input type="text" readOnly 
                    style={{ fontSize: 18}} 
                    placeholder='RUT Empresa' 
                    className='form-control input-blue' 
                    {...register('rutEmpresa')}
                    />
                </div>
              </div>

              <div className="form-comprobante-row">
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Precio Total Neto
                    <span style={{color: '#c5c5c5ff'}}>(automático)</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input type='text' className='form-control' readOnly 
                      style={{background: '#f9fafb', fontSize: 18}} 
                      {...register('precioNeto')} 
                    />
                  </div>
                </div>
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">
                    Fecha de Emisión<span className="required">*</span>
                  </label>
                    <input type='date'
                      style={{ fontSize: 18, fontWeight: 'bold'}} 
                      min={fechaMin ?? undefined}
                      max={fechaMax ?? undefined}
                      className={`form-control ${errors.fecha ? 'is-invalid' : ''}`}
                      {...register('fecha')}
                    />
                  {errors.fecha && (
                    <div className="invalid-feedback">{errors.fecha.message}</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Contenedor Derecha */}
          <div className='contenedor-nc'>
            <div className='card-nc'>
              <h5>Productos Asociados</h5>
              <DataTable 
                value={productos} 
                header={renderHeaderProductos()} 
                dataKey='productoId'
                size='small' 
                selectionMode="checkbox" 
                className='prod-nc-table'
                paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                selection={selectedProductos}
                rowClassName={(row) => (canDescontar(row) ? '' : 'row-no-stock')}
                onSelectionChange={ (e) => {
                  // Filtra: SOLO productos permitidos pueden quedar seleccionados
                  const permitidos = (e.value || []).filter(canDescontar);
                  // Aviso si hubo intentos de seleccionar no permitidos
                  if ((e.value?.length || 0) > permitidos.length) {
                    toast.current?.show({
                      severity: 'warn',
                      summary: 'Sin stock',
                      detail: 'Se omitieron productos sin stock suficiente.',
                      life: 2500
                    });
                  }
                  setSelectedProductos(permitidos);
                  setCantidades(prev => {
                    const next = { ...prev };
                    // borra los que ya no están seleccionados
                    Object.keys(next).forEach(id => {
                      if (!permitidos.some(p => p.productoId === Number(id))) delete next[id];
                    });
                    permitidos.forEach(p => {
                      if (next[p.productoId] == null) next[p.productoId] = 1;
                    });
                    return next;
                  });
                }}
                key={selectedProductos.map(p=>p.productoId).join(',') || 'none'}
                stripedRows 
                scrollable={dtResponsive === 'scroll'}
                scrollHeight={isPhone ? '45vh' : isTablet ? '55vh' : '30rem'}
                tableStyle={{ fontSize: isPhone ? '0.8rem' : '0.9rem', minWidth: '20rem' }}
                filters={filters}
                filteredvalue={filteredvalue.length > 0 ? filteredvalue : null}
                filterDisplay="menu"
                globalFilterFields={['codigoBarra', 'nombre', 'marca', 'unidad', 'medida', 'precioUnitario', 'bodega']}
                emptyMessage='No hay productos disponibles.'
              >
                <Column selectionMode="multiple" headerStyle={{ width: '1rem' }}></Column>
                <Column header="Código Barra" field="codigoBarra" ></Column>
                <Column header="Nombre" field="nombre" ></Column>
                <Column header="Marca" field="marca" ></Column>
                <Column header="Precio Unitario" field="precioUnitario" align={'center'} body={(rowData) => formatearMoneda(rowData.precioUnitario)} ></Column>
                <Column header="Unidad" align={'center'} body={(rowData) => `${rowData.medida} ${rowData.unidad}`}></Column>
                <Column header="Bodega" field="bodegaInventario" ></Column>
                <Column header="Stock" align={'center'} body={(rowData) => `${rowData.stockInventario} ${rowData.unidad}`}></Column>
                <Column header="Cantidad" field="cantidadInventario"  align={'center'} style={{ textAlign: 'center !important', justifyContent: 'center'}}></Column>
                <Column header="Descontar" align={'center'} body={descontarTemplate}></Column>
              </DataTable>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  )
}